import React, { useEffect, useRef, createContext, useState } from 'react';
import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';
import find from 'lodash/find';
import isEqual from 'lodash/isEqual';

import AccountUnderReviewWidget from '../components/AccountUnderReviewWidget';
import UnauthorizedAccessWidget from '../components/UnauthorizedAccessWidget';
import JobcaseAgreementModal from '../components/JobcaseAgreementModal';

import {
  getIsEmployerProfile,
  getIsLoading,
  getCurrentEmployer,
  getEmployerGrantsById,
  getJobcaseEmployerAdminLevel,
  getAllEmployerGrants,
  getHasAllEmployersGrants,
} from '../redux/nodes/features/employers/selectors';
import { getIsAuthenticated, getProfileKey } from '../redux/nodes/authentication/selectors';
import {
  PAGES,
  routeToEmployerCenterLogin,
  GLOBAL_ROUTES,
  routeToVerification,
} from '../redux/nodes/router/actions';
import { getEmployers } from '../redux/nodes/entities/employers/selectors';
import { setCurrentEmployer } from '../redux/nodes/features/employers/actions';
import { getOrganization } from '../redux/nodes/features/settings/actions';
import { getOrganizationById } from '../redux/nodes/features/settings/selectors';

import GrantsAPI from '../utils/api/grantsAPI';
import reporter from '../utils/reporter';
import { hasAdminAccessForUserRolesPermissions } from '../utils/grants';
import {
  UPWARD_ACCESS,
  buildReviewKey,
  effectiveUpwardAccess,
  isPartnerAccessBlocked,
  isPartnerReviewDecisionMissing,
  resolveUpwardAccess,
  shouldShowAccountUnderReview,
  shouldHoldRendering,
} from '../utils/partnerReviewGate';
import { useSelector } from '../utils/hooks';
import { dynamicRoute } from '../utils/routing';
import { isUUID } from '../utils/hiringEvents';
import { ALL_EMPLOYERS_ID, JOBCASE_EMPLOYER_ID, NAME_TO_ROUTE_MAP, ROUTE_TO_KEY_MAP } from '../utils/constants/employerNavigation';
import { isObjectEmpty } from '../utils/utils';
import { PATHNAME_REGEX } from '../utils/constants/regex';
import { ORGANIZATION_STATUS } from '../utils/constants/settings';
import { EMPLOYER_ROLE_ACCESS_LEVELS } from '../constants';

export const WithGrantsContext = createContext();

const withGrants = (Component) => {
  const WrapperComponent = () => {
    const dispatch = useDispatch();

    const router = useRouter();
    const employerId = router?.query?.employer;
    const subEmployerId = router?.query?.subEmployer;

    const isLoading = useSelector(getIsLoading);
    const isAuthenticated = useSelector(getIsAuthenticated);

    const currentEmployerId = useSelector(getCurrentEmployer);
    const isEmployerProfile = useSelector(getIsEmployerProfile);

    const employerGrants = useSelector(getEmployerGrantsById(currentEmployerId));
    const organization = useSelector(getOrganizationById(currentEmployerId));
    const isLegacyEmployer = organization?.is_legacy_employer;
    const jobcaseEmployerAdminLevel = useSelector(getJobcaseEmployerAdminLevel);
    const allowedRoutes = dynamicRoute(employerGrants, !!jobcaseEmployerAdminLevel, !isLegacyEmployer);
    const hasAllEmployersGrants = useSelector(getHasAllEmployersGrants);
    const originalEmployersData = useSelector(getEmployers);
    const allEmployerGrants = useSelector(getAllEmployerGrants);

    const pathname = router?.pathname?.replace(PATHNAME_REGEX, '');
    const currentRouteKey = ROUTE_TO_KEY_MAP[pathname];
    const currentRoute = find(allowedRoutes, val => val.key === currentRouteKey);

    // LUT-524 / LUT-564: partner-request accounts (organization.is_partner persisted at signup)
    // must not see the Employer Center until the request is approved. org-service owns that
    // decision and ships it as `partner_review_approved` on the organization payload; only an
    // explicit false blocks, absent fails open and is reported below. While not approved, upward
    // roles for THIS organization's job-feed org lift the wall for the requester. Approvers are
    // exempt with the same effective-admin predicate the User Roles page uses (Jobcase employer
    // admin level OR the all-employer User Roles admin grants), since they are the ones who
    // approve. Decision logic lives in utils/partnerReviewGate.js where it is unit-tested.
    const loggedInProfileKey = useSelector(getProfileKey);
    const isApprover = Object.values(EMPLOYER_ROLE_ACCESS_LEVELS).includes(jobcaseEmployerAdminLevel)
      || hasAdminAccessForUserRolesPermissions(allEmployerGrants['*']);
    const partnerAccessBlocked = isPartnerAccessBlocked({ isAuthenticated, isApprover, organization });
    // Low-volume telemetry: a partner organization whose payload has no usable decision means a
    // rollback or a missed mapper upstream. The gate fails open for the user; this makes it visible.
    const reportedMissingDecisionFor = useRef(new Set());
    useEffect(() => {
      const organizationId = organization?.id;
      if (!organizationId || !isPartnerReviewDecisionMissing(organization)) return;
      if (reportedMissingDecisionFor.current.has(organizationId)) return;
      reportedMissingDecisionFor.current.add(organizationId);
      reporter.report('JobcaseECPartnerReviewDecisionMissing', {
        employer_id: organizationId,
        value_type: organization.partner_review_approved === null ? 'null' : typeof organization.partner_review_approved,
      }).catch(() => {});
    }, [organization?.id, organization?.is_partner, organization?.partner_review_approved]);
    const upwardJobFeedId = organization?.upward_job_feed_id;
    // The stored answer carries the viewer+organization identity it was fetched for and
    // synchronously counts as UNKNOWN whenever the identity differs - effects run after
    // rendering, so a plain reset-in-effect would let the previous organization's GRANTED
    // answer render the destination dashboard once when switching partner orgs.
    const reviewKey = buildReviewKey({
      profileKey: loggedInProfileKey,
      employerId: currentEmployerId,
      upwardJobFeedId,
    });
    const [upwardAccessResult, setUpwardAccessResult] = useState(null);
    const upwardAccess = effectiveUpwardAccess(upwardAccessResult, reviewKey);
    useEffect(() => {
      let cancelled = false;
      if (!partnerAccessBlocked || !loggedInProfileKey) return undefined;
      GrantsAPI()
        .getUpwardGrantsByProfile(loggedInProfileKey)
        .then((data) => {
          if (!cancelled) {
            setUpwardAccessResult({ reviewKey, access: resolveUpwardAccess(data, upwardJobFeedId) });
          }
        })
        .catch(() => {
          if (!cancelled) setUpwardAccessResult({ reviewKey, access: UPWARD_ACCESS.UNAVAILABLE });
        });
      return () => { cancelled = true; };
    }, [partnerAccessBlocked, loggedInProfileKey, reviewKey]);
    // Re-checked on every page load, so approval lifts the gate on the next sign-in or
    // navigation with no further changes.
    const showAccountUnderReview = shouldShowAccountUnderReview(partnerAccessBlocked, upwardAccess);
    const holdRenderingForReviewCheck = shouldHoldRendering(partnerAccessBlocked, upwardAccess);

    const [hasAccessToPage, setHasAccessToPage] = useState(isEqual(pathname, PAGES.EMPLOYERS) || isEqual(pathname, PAGES.NOTIFICATIONS) || !!currentRoute);
    const handleRoute = (item, query = {}) => {
      const { key } = item;
      const routeHandler = NAME_TO_ROUTE_MAP[key];
      routeHandler(query);
    };

    const routeToFirstAllowedRoute = async (employerId = '') => {
      const organization = await dispatch(getOrganization({ organizationId: employerId }));
      if (!jobcaseEmployerAdminLevel && !hasAllEmployersGrants) {
        if (organization?.data?.status === ORGANIZATION_STATUS.PENDING_REQUEST_VERIFICATION) {
          routeToVerification({ organization_id: employerId });
          return;
        }
      }

      const employerGrants = allEmployerGrants['*'] || allEmployerGrants[employerId] || [];
      const allowedRoutes = dynamicRoute(
        employerGrants,
        !!jobcaseEmployerAdminLevel,
        !organization?.data?.is_legacy_employer,
      );

      const currentRoute = allowedRoutes.find(val => val.key === currentRouteKey);

      setHasAccessToPage(isEqual(pathname, PAGES.EMPLOYERS) || isEqual(pathname, PAGES.NOTIFICATIONS) || !!currentRoute);
      if (!jobcaseEmployerAdminLevel && currentEmployerId === ALL_EMPLOYERS_ID) {
        setHasAccessToPage(false);
      }
      const isEmployerPage = isObjectEmpty(currentRoute) && isEqual(pathname, PAGES.EMPLOYERS);
      const shouldRouteToFirstRouteIfSuperAdmin = isObjectEmpty(currentRoute) && !!jobcaseEmployerAdminLevel;

      if ((isEmployerPage || shouldRouteToFirstRouteIfSuperAdmin || isObjectEmpty(currentRoute)) && !isEqual(pathname, PAGES.NOTIFICATIONS)) {
        const [firstRoute] = allowedRoutes;
        if (!isObjectEmpty(firstRoute)) {
          handleRoute(firstRoute, { employer: employerId });
        }
      }
    };

    const appendEmployerIdInRouteParams = async (employerId = '', additionalParams = {}) => {
      let queryObject = router.query;
      const hasAdditionalParams = Object.keys(additionalParams).length !== 0;
      // Only replace the Employer ID in the URL if it is different from the one already set
      if (router?.query?.employer !== employerId) {
        queryObject = { ...queryObject, employer: employerId };
      }
      if (hasAdditionalParams) {
        queryObject = { ...queryObject, ...additionalParams };
      }
      if (queryObject !== router.query) {
        router.replace({
          query: queryObject,
        });
      }
    };

    useEffect(() => {
      if (!isAuthenticated) {
        routeToEmployerCenterLogin(router.pathname);
      }
    }, [isAuthenticated]);

    useEffect(() => {
      if (!employerId) {
        appendEmployerIdInRouteParams(currentEmployerId);
      }
    }, [employerId]);

    useEffect(() => {
      const employers = Object.values(originalEmployersData);
      let employer;

      if (isUUID(employerId)) {
        employer = employers.find(employer => employer?.id === employerId);
      } else {
        employer = employers.find(employer => employer?.name === employerId);
      }

      if (!isObjectEmpty(employer)) {
        dispatch(setCurrentEmployer(employerId, subEmployerId));
        if (jobcaseEmployerAdminLevel || !Object.values(GLOBAL_ROUTES).includes(pathname, 0)) {
          routeToFirstAllowedRoute(employerId);
        }
      } else if (hasAllEmployersGrants && !currentEmployerId) {
        dispatch(setCurrentEmployer(JOBCASE_EMPLOYER_ID));
      }
    }, [employerId, subEmployerId]);

    const contextValues = {
      appendEmployerIdInRouteParams,
      handleRoute,
      isLegacyEmployer,
      allowedRoutes,
      routeToFirstAllowedRoute,
    };

    if (showAccountUnderReview || holdRenderingForReviewCheck) {
      return (
        <WithGrantsContext.Provider value={contextValues}>
          {showAccountUnderReview && <AccountUnderReviewWidget />}
        </WithGrantsContext.Provider>
      );
    }

    return (
      <WithGrantsContext.Provider value={contextValues}>
        {jobcaseEmployerAdminLevel || (!isLoading && isEmployerProfile && hasAccessToPage) ? (
          <>
            <Component />
            <JobcaseAgreementModal />
          </>
        ) : (
          <>
            {isAuthenticated && <UnauthorizedAccessWidget />}
          </>
        )}
      </WithGrantsContext.Provider>
    );
  };

  return WrapperComponent;
};

export default withGrants;
